/*
---------------------------------------------------------
Copyright © 2026 Daniel Woik (DW E-Commerce). All rights reserved.
Custom-coded by DW E-Commerce. This code is licensed for use on
this store only, as part of the DW E-Commerce optimization service.
Copying, reselling, redistributing or using it on any other store
is not permitted without written permission.
---------------------------------------------------------
DW Kit:   Product Cross-Sell
Version:  1.2.0
Updated:  2026-10-14 — Type modes: related, complementary only, complementary with related when empty; desktop edge stops at the Buy Box column.
Source:   DW-Component-Library/Kits/Product-Cross-Sell

FUNCTION LOCKED. <dw-product-cross-sell> loads Shopify product
recommendations for the product on the page as normal DW product cards
(sections/dw-product-cross-sell-fetch.liquid) into its <dw-slider>,
applies the block filters (in cart, sold out, max price) and shows the
block only when at least one product is left. Complementary with no
result falls back to related when the block setting is on.
Products added to the cart leave the row on the next cart update
(event dw:cart:updated from the Cart Drawer kit).
---------------------------------------------------------
*/
(function () {
  if (window.customElements.get('dw-product-cross-sell')) return;

  class DWProductCrossSell extends HTMLElement {
    connectedCallback() {
      if (this._started) return;
      this._started = true;
      this._onCart = this.onCartUpdated.bind(this);
      document.addEventListener('dw:cart:updated', this._onCart);
      this._onResize = () => this.updateEdge();
      window.addEventListener('resize', this._onResize, { passive: true });
      if ('ResizeObserver' in window) {
        this._edgeObserver = new ResizeObserver(this._onResize);
        this._edgeObserver.observe(this);
      }
      this.start();
    }

    disconnectedCallback() {
      document.removeEventListener('dw:cart:updated', this._onCart);
      window.removeEventListener('resize', this._onResize);
      if (this._edgeObserver) this._edgeObserver.disconnect();
    }

    /* "Run to the screen edge while swiping": measure the distance from the block to the screen edges,
       or to the edges of the nearest parent that clips its content, and hand it to the CSS. */
    updateEdge() {
      const mode = this.dataset.edge || 'off';
      const desktop = window.matchMedia('(min-width: 750px)').matches;
      const wanted = mode === 'all' || (mode === 'mobile' && !desktop);
      const rect = this.getBoundingClientRect();
      if (!wanted || this.hidden || !rect.width) {
        this.classList.remove('dw-cross-sell--edge');
        this.style.removeProperty('--dw-cs-edge-left');
        this.style.removeProperty('--dw-cs-edge-right');
        return;
      }
      let leftEdge = 0;
      let rightEdge = document.documentElement.clientWidth;
      for (let el = this.parentElement; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        const style = getComputedStyle(el);
        const box = el.getBoundingClientRect();
        /* Desktop: stop at the Buy Box column, the first parent with side padding (run through its padding only). */
        if (desktop && (parseFloat(style.paddingLeft) > 0 || parseFloat(style.paddingRight) > 0)) {
          leftEdge = box.left + (parseFloat(style.borderLeftWidth) || 0);
          rightEdge = box.right - (parseFloat(style.borderRightWidth) || 0);
          break;
        }
        if (style.overflowX !== 'visible' || /paint|strict|content/.test(style.contain || '')) {
          leftEdge = Math.max(leftEdge, box.left);
          rightEdge = Math.min(rightEdge, box.right);
          break;
        }
      }
      const left = Math.max(0, Math.floor(rect.left - leftEdge));
      const right = Math.max(0, Math.floor(rightEdge - rect.right));
      this.style.setProperty('--dw-cs-edge-left', left + 'px');
      this.style.setProperty('--dw-cs-edge-right', right + 'px');
      this.classList.toggle('dw-cross-sell--edge', left > 0 || right > 0);
    }

    async start() {
      const limit = Math.max(1, parseInt(this.dataset.limit, 10) || 6);
      const inCart = this.dataset.hideInCart === 'true' ? await this.cartProductIds() : new Set();
      const mode = this.dataset.intent || 'related';
      let intent = mode === 'related' ? 'related' : 'complementary';
      let items = await this.fetchItems(intent, limit);
      items = items.filter((item) => this.keep(item, inCart));
      if (!items.length && mode === 'complementary_related') {
        intent = 'related';
        items = (await this.fetchItems(intent, limit)).filter((item) => this.keep(item, inCart));
      }
      this.render(items.slice(0, limit));
    }

    async cartProductIds() {
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const cart = await fetch(root + 'cart.js', { credentials: 'same-origin' }).then((r) => r.json());
        return new Set((cart.items || []).map((item) => String(item.product_id)));
      } catch (e) {
        return new Set();
      }
    }

    async fetchItems(intent, limit) {
      const productId = this.dataset.productId;
      const base = this.dataset.url;
      const sectionId = this.dataset.fetchSectionId;
      if (!productId || !base || !sectionId) return [];
      /* Ask for a few more, because products in the cart and filtered ones are left out. */
      const ask = Math.min(10, limit + 4);
      const url = base + '?product_id=' + encodeURIComponent(productId) + '&limit=' + ask + '&section_id=' + encodeURIComponent(sectionId) + '&intent=' + encodeURIComponent(intent);
      try {
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return [];
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const root = doc.querySelector('[data-dw-cross-sell-root]');
        return root ? Array.from(root.querySelectorAll('.dw-slider__item')) : [];
      } catch (e) {
        return [];
      }
    }

    keep(item, inCart) {
      if (String(item.dataset.productId) === String(this.dataset.productId)) return false;
      if (inCart.has(String(item.dataset.productId))) return false;
      if (this.dataset.hideSoldOut === 'true' && item.dataset.available === 'false') return false;
      if (this.dataset.enableMaxPrice === 'true') {
        const max = Math.round(parseFloat(this.dataset.maxPrice || '0') * 100);
        if (max > 0 && parseInt(item.dataset.price, 10) > max) return false;
      }
      return true;
    }

    setVisible(on) {
      this.classList.toggle('dw-cross-sell--has-products', on);
      /* In the theme editor the block stays visible with its note, so it can be found and set up. */
      if (!(window.Shopify && window.Shopify.designMode)) this.hidden = !on;
      this.updateEdge();
    }

    render(items) {
      const slider = this.querySelector('dw-slider');
      const track = this.querySelector('[data-dw-slider-track]');
      if (!track) return;
      track.innerHTML = '';
      items.forEach((item) => track.appendChild(document.importNode(item, true)));
      this.setVisible(items.length > 0);
      if (slider && typeof slider.refresh === 'function') slider.refresh();
    }

    /* A product that is now in the cart leaves the row; an empty row hides the block. */
    onCartUpdated(event) {
      if (this.dataset.hideInCart !== 'true') return;
      const cart = event.detail && event.detail.cart;
      if (!cart || !cart.items) return;
      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      const track = this.querySelector('[data-dw-slider-track]');
      if (!track) return;
      track.querySelectorAll('.dw-slider__item').forEach((item) => {
        if (inCart.has(String(item.dataset.productId))) item.remove();
      });
      this.setVisible(track.querySelectorAll('.dw-slider__item').length > 0);
      const slider = this.querySelector('dw-slider');
      if (slider && typeof slider.refresh === 'function') slider.refresh();
    }
  }

  window.customElements.define('dw-product-cross-sell', DWProductCrossSell);
})();
